import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaClock,
  FaRupeeSign,
  FaChevronDown,
  FaChevronUp,
  FaTag,
} from "react-icons/fa";

const Packages = () => {
  const server_url = localStorage.getItem("server_url");
  const navigate = useNavigate();

  // Get user data from localStorage
  const garage_id = localStorage.getItem("grage_id");
  const grage_name = localStorage.getItem("grage_name");
  const grage_email = localStorage.getItem("grage_email");
  const grage_number = localStorage.getItem("grage_number");
  const company_store_name = localStorage.getItem("company_store_name");

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedPackages, setExpandedPackages] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [activeMembership, setActiveMembership] = useState(null);
  const [showNoMembershipModal, setShowNoMembershipModal] = useState(false);
  const [showActiveMembershipModal, setShowActiveMembershipModal] =
    useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    fetchPackages();
    checkActiveMembership();

    // Load Razorpay script
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append(
        "company_id",
        localStorage.getItem("grage_city_id") || "2"
      );

      const response = await axios.post(
        `${server_url}package_list.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.status === "1") {
        setPackages(response.data.result || []);
      } else {
        toast.error(response.data.error || "No packages available");
        setPackages([]);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load packages");
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const checkActiveMembership = async () => {
    if (!garage_id) return;

    try {
      const formData = new FormData();
      formData.append("garage_id", garage_id);

      const response = await axios.post(
        `${server_url}get_membership.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.status === "1") {
        setActiveMembership(response.data.membership);
      } else {
        setActiveMembership(null);
      }
    } catch (error) {
      console.error("Error checking membership:", error);
      setActiveMembership(null);
    }
  };

  const parseAllFeatures = (remark) => {
    if (!remark) return [];

    const delimiters = [",", ";", "|", "•", "✓", "✔", "- ", "* ", "~ "];

    for (const delimiter of delimiters) {
      const points = remark
        .split(delimiter)
        .map((point) => point.trim())
        .filter((point) => point.length > 2);

      if (points.length > 1) {
        return points;
      }
    }

    return [remark];
  };

  const toggleExpand = (packageId) => {
    setExpandedPackages((prev) => ({
      ...prev,
      [packageId]: !prev[packageId],
    }));
  };

  const handleSelectPackage = (pkg) => {
    if (!garage_id) {
      toast.error("Please login to continue");
      navigate("/login");
      return;
    }

    setSelectedPackage(pkg);

    // Check if user has active membership
    if (activeMembership) {
      // Show active membership modal
      setShowActiveMembershipModal(true);
    } else {
      // Show confirmation for users without membership
      setShowNoMembershipModal(true);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!selectedPackage || !window.Razorpay) {
      toast.error("Payment gateway is not loaded. Please refresh the page.");
      return;
    }

    setProcessing(selectedPackage.package_id);
    setShowNoMembershipModal(false);
    setShowActiveMembershipModal(false);

    try {
      // Step 1: Create membership record
      const membershipFormData = new FormData();
      membershipFormData.append("garage_id", garage_id);
      membershipFormData.append("garage_name", grage_name);
      membershipFormData.append("garage_email", grage_email);
      membershipFormData.append("garage_phone", grage_number);
      membershipFormData.append(
        "company_id",
        localStorage.getItem("grage_city_id") || "2"
      );
      membershipFormData.append("company_name", company_store_name);
      membershipFormData.append("package_id", selectedPackage.package_id);
      membershipFormData.append("package_name", selectedPackage.package_name);
      membershipFormData.append("package_price", selectedPackage.package_price);
      membershipFormData.append("package_days", selectedPackage.package_days);
      membershipFormData.append(
        "start_date",
        new Date().toISOString().split("T")[0]
      );

      const membershipRes = await axios.post(
        `${server_url}create_membership.php`,
        membershipFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10000,
        }
      );


      if (membershipRes.data.status !== "1") {
        throw new Error(
          membershipRes.data.error || "Failed to create membership"
        );
      }

      const membershipId = membershipRes.data.membership_id;

      // Step 2: Create Razorpay order
      const orderFormData = new FormData();
      const amountInPaise = Math.round(selectedPackage.package_price * 100);
      orderFormData.append("amount", amountInPaise);
      orderFormData.append("currency", "INR");
      orderFormData.append("receipt", `MEM${membershipId}`);
      orderFormData.append("membership_id", membershipId);
      orderFormData.append("package_name", selectedPackage.package_name);

      const orderRes = await axios.post(
        `${server_url}create_razorpay_order.php`,
        orderFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10000,
        }
      );


      if (orderRes.data.status !== "1") {
        throw new Error(
          orderRes.data.error || "Failed to create payment order"
        );
      }

      const orderData = orderRes.data.order;

      if (!orderData || !orderData.id || !orderRes.data.key_id) {
        throw new Error("Invalid order data received");
      }

      // Step 3: Open Razorpay payment modal
      const options = {
        key: orderRes.data.key_id,
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: company_store_name || "Garage Management System",
        description: `Package: ${selectedPackage.package_name} - ${selectedPackage.package_days} Days`,
        order_id: orderData.id,
        handler: async function (response) {

          setShowConfirmation(true);
          setPaymentSuccess(true);

          await verifyPayment(response, membershipId);

          setTimeout(() => {
            navigate("/membership");
          }, 3000);
        },
        prefill: {
          name: grage_name || "",
          email: grage_email || "",
          contact: grage_number || "",
        },
        theme: {
          color: "#00409e",
        },
        modal: {
          ondismiss: function () {
            setProcessing(null);
            toast.error("Payment cancelled by user");
          },
        },
        notes: {
          membership_id: membershipId,
          garage_id: garage_id,
          package: selectedPackage.package_name,
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setProcessing(null);
      });

      rzp.open();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.message || "Payment failed");
      setProcessing(null);
    }
  };

  const verifyPayment = async (response, membershipId) => {
    try {

      const verifyFormData = new FormData();
      verifyFormData.append(
        "razorpay_payment_id",
        response.razorpay_payment_id
      );
      verifyFormData.append("razorpay_order_id", response.razorpay_order_id);
      verifyFormData.append("razorpay_signature", response.razorpay_signature);
      verifyFormData.append("membership_id", membershipId);

      const verifyRes = await axios.post(
        `${server_url}verify_payment.php`,
        verifyFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10000,
        }
      );


      if (verifyRes.data.status === "1") {

        // Set status to "waiting" if user already has active membership
        if (activeMembership) {
          localStorage.setItem("membership_status", "waiting");
        } else {
          localStorage.setItem("membership_status", "active");
        }

        localStorage.setItem("current_membership_id", membershipId);
        localStorage.setItem("payment_status", "success");
        localStorage.setItem("payment_date", new Date().toISOString());
      } else {
        console.error("Payment verification failed:", verifyRes.data.error);
        toast.error("Payment verification failed. Please contact support.");
      }
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("Payment verification failed. Please contact support.");
    }
  };

  // Calculate discount percentage
  const calculateDiscount = (originalPrice, offerPrice) => {
    if (!originalPrice || !offerPrice || originalPrice <= offerPrice) return 0;
    const discount = ((originalPrice - offerPrice) / originalPrice) * 100;
    return Math.round(discount);
  };

  // No Membership Confirmation Modal
  const NoMembershipModal = () => {
    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Confirm Membership Plan</h2>
          </div>
          <div style={styles.modalBody}>
            <p style={styles.modalMessage}>
              Are you sure you want to continue with this membership plan?
            </p>
            <div style={styles.modalButtons}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowNoMembershipModal(false);
                  setProcessing(null);
                }}
              >
                Cancel
              </button>
              <button
                style={styles.primaryButton}
                onClick={handleConfirmPurchase}
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Active Membership Found Modal
  const ActiveMembershipModal = () => {
    if (!activeMembership) return null;

    return (
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Active Membership Found</h2>
          </div>
          <div style={styles.modalBody}>
            <p style={styles.modalMessage}>
              You already have an active membership. Are you sure you want to
              purchase a new one?
              <br />
              <strong>
                The new membership will start after your current plan expires.
              </strong>
            </p>

            <div style={styles.modalButtons}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowActiveMembershipModal(false);
                  setProcessing(null);
                }}
              >
                Cancel
              </button>
              <button
                style={styles.primaryButton}
                onClick={handleConfirmPurchase}
              >
                Purchase Now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Payment Confirmation Modal
  const ConfirmationModal = () => {
    return (
      <div style={styles.confirmationOverlay}>
        <div style={styles.confirmationModal}>
          <div style={styles.confirmationContent}>
            <div style={styles.confirmationIcon}>✓</div>
            <h2 style={styles.confirmationTitle}>
              {paymentSuccess ? "Payment Successful!" : "Processing..."}
            </h2>
            <p style={styles.confirmationText}>
              {paymentSuccess
                ? activeMembership
                  ? "Your new membership is queued and will start after your current plan expires."
                  : "Your membership has been activated successfully!"
                : "Please wait while we verify your payment..."}
            </p>
            <div style={styles.confirmationLoader}>
              <div style={styles.loadingBar}>
                <div style={styles.loadingProgress}></div>
              </div>
              <p style={styles.redirectText}>
                Redirecting to membership page...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            {company_store_name ? `${company_store_name} Packages` : "Packages"}
          </h1>
        </div>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading packages...</p>
        </div>
      </div>
    );
  }

  if (packages.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            {company_store_name ? `${company_store_name} Packages` : "Packages"}
          </h1>
        </div>
        <div style={styles.empty}>
          <p>No packages available at the moment</p>
          <button style={styles.refreshBtn} onClick={fetchPackages}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Modals */}
      {showNoMembershipModal && <NoMembershipModal />}
      {showActiveMembershipModal && <ActiveMembershipModal />}
      {showConfirmation && <ConfirmationModal />}

      {/* Header */}
      {/* Packages List */}
      <div style={styles.packagesContainer}>
        {packages.map((pkg) => {
          const allFeatures = parseAllFeatures(pkg.package_remark);
          const isProcessing = processing === pkg.package_id;
          const isExpanded = expandedPackages[pkg.package_id];
          const pricePerDay =
            pkg.package_days > 0
              ? (pkg.package_price / pkg.package_days).toFixed(2)
              : pkg.package_price;

          const hasManyFeatures = allFeatures.length > 50;
          const displayFeatures =
            isExpanded || !hasManyFeatures ? allFeatures : allFeatures;

          // Calculate discount if offer price exists
          const discount = calculateDiscount(
            pkg.package_offer_price,
            pkg.package_price
          );

          return (
            <div key={pkg.package_id} style={styles.packageCard}>
              {/* Package Header */}
              <div style={styles.cardHeader}>
                <div>
                  <h2 style={styles.packageName}>{pkg.package_name}</h2>
                  <div style={styles.duration}>
                    <FaClock style={{ marginRight: 5 }} />
                    <span>{pkg.package_days} Days</span>
                    <span style={styles.perDay}>(₹{pricePerDay}/day)</span>
                  </div>
                </div>
                <div style={styles.priceContainer}>
                  {/* Discount Badge */}
                  {discount > 0 && (
                    <div style={styles.discountBadge}>
                      <FaTag style={{ marginRight: 3, fontSize: 10 }} />
                      {discount}% OFF
                    </div>
                  )}

                  {/* Original Price (Strikethrough) */}
                  {pkg.package_offer_price && pkg.package_offer_price > pkg.package_price && (
                    <div style={styles.originalPrice}>
                      <FaRupeeSign style={{ fontSize: 14 }} />
                      <s>{pkg.package_offer_price}</s>
                    </div>
                  )}

                  {/* Current Price */}
                  <div style={styles.currentPrice}>
                    <FaRupeeSign style={{ fontSize: 20 }} />
                    <span style={{ fontSize: 32, fontWeight: "bold" }}>
                      {pkg.package_price}
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              {allFeatures.length > 0 && (
                <div style={styles.featuresSection}>
                  <div style={styles.featuresHeader}>
                    <h3 style={styles.featuresTitle}>Package Includes:</h3>
                  </div>

                  <div style={styles.featuresContainer}>
                    {displayFeatures.map((feature, index) => (
                      <div key={index} style={styles.featureItem}>
                        <FaCheck style={styles.checkIcon} />
                        <span style={styles.featureText}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {hasManyFeatures && (
                    <button
                      style={styles.showMoreBtn}
                      onClick={() => toggleExpand(pkg.package_id)}
                    >
                      {isExpanded ? (
                        <>
                          <FaChevronUp style={{ marginRight: 5 }} />
                          Show Less
                        </>
                      ) : (
                        <>
                          <FaChevronDown style={{ marginRight: 5 }} />
                          Show {allFeatures.length - 5} More Features
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Select Button */}
              <button
                style={{
                  ...styles.selectBtn,
                  ...(isProcessing && styles.processingBtn),
                }}
                onClick={() => handleSelectPackage(pkg)}
                disabled={isProcessing || showConfirmation}
              >
                {isProcessing ? "Processing..." : "Select & Pay"}
              </button>
            </div>
          );
        })}
      </div>

      
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: 16,
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: "600",
    color: "#00409e",
    margin: 0,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    margin: 0,
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "50vh",
  },
  spinner: {
    width: 40,
    height: 40,
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #00409e",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: 16,
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "#666",
  },
  refreshBtn: {
    padding: "10px 20px",
    backgroundColor: "#00409e",
    color: "white",
    border: "none",
    borderRadius: 6,
    marginTop: 15,
    cursor: "pointer",
  },
  packagesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  packageCard: {
    backgroundColor: "white",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
    position: "relative",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: "1px solid #eee",
  },
  packageName: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333",
    margin: 0,
    marginBottom: 6,
  },
  duration: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    color: "#666",
  },
  perDay: {
    marginLeft: 8,
    fontSize: 12,
    color: "#888",
  },
  priceContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  discountBadge: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#ff4757",
    color: "white",
    padding: "4px 8px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 5,
  },
  originalPrice: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    color: "#999",
    textDecoration: "line-through",
    marginBottom: 2,
  },
  currentPrice: {
    display: "flex",
    alignItems: "center",
    color: "#00409e",
  },
  featuresSection: {
    marginBottom: 20,
  },
  featuresHeader: {
    marginBottom: 12,
  },
  featuresTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    margin: 0,
  },
  featuresContainer: {
    overflowY: "auto",
    paddingRight: 5,
    marginBottom: 10,
  },
  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: 10,
    fontSize: 14,
    color: "#555",
    lineHeight: 1.4,
    padding: "6px 0",
    borderBottom: "1px dashed #eee",
  },
  checkIcon: {
    color: "#28a745",
    marginRight: 10,
    marginTop: 2,
    fontSize: 14,
    flexShrink: 0,
  },
  featureText: {
    flex: 1,
  },
  showMoreBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "8px 12px",
    backgroundColor: "transparent",
    color: "#00409e",
    border: "1px solid #00409e",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: "500",
    cursor: "pointer",
    marginTop: 10,
    transition: "all 0.2s",
  },
  selectBtn: {
    width: "100%",
    padding: "14px",
    backgroundColor: "#00409e",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
    marginTop: 10,
  },
  processingBtn: {
    backgroundColor: "#6c757d",
    cursor: "not-allowed",
    opacity: 0.8,
  },
  footer: {
    marginTop: 30,
    paddingTop: 20,
    borderTop: "1px solid #ddd",
    textAlign: "center",
  },
  footerText: {
    fontSize: 12,
    color: "#666",
    margin: "5px 0",
  },
  // Modal Styles
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: 16,
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: 12,
    width: "100%",
    maxWidth: 400,
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "20px 20px 10px",
    textAlign: "center",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#00409e",
    margin: 0,
  },
  modalBody: {
    padding: "10px 20px 20px",
  },
  modalMessage: {
    fontSize: 14,
    color: "#666",
    lineHeight: 1.5,
    textAlign: "center",
    marginBottom: 20,
  },
  modalButtons: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
  },
  primaryButton: {
    padding: "12px 24px",
    backgroundColor: "#00409e",
    color: "white",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: "600",
    cursor: "pointer",
    flex: 1,
  },
  secondaryButton: {
    padding: "12px 24px",
    backgroundColor: "#f8f9fa",
    color: "#495057",
    border: "1px solid #dee2e6",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: "600",
    cursor: "pointer",
    flex: 1,
  },
  // Confirmation Modal Styles
  confirmationOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1001,
  },
  confirmationModal: {
    backgroundColor: "white",
    borderRadius: 16,
    padding: 30,
    maxWidth: 400,
    width: "90%",
    textAlign: "center",
    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
  },
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  confirmationIcon: {
    width: 80,
    height: 80,
    backgroundColor: "#28a745",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 40,
    color: "white",
    fontWeight: "bold",
    marginBottom: 20,
  },
  confirmationTitle: {
    fontSize: 24,
    fontWeight: "600",
    color: "#28a745",
    margin: "0 0 10px 0",
  },
  confirmationText: {
    fontSize: 16,
    color: "#666",
    marginBottom: 25,
    lineHeight: 1.4,
  },
  confirmationLoader: {
    width: "100%",
  },
  loadingBar: {
    width: "100%",
    height: 6,
    backgroundColor: "#e9ecef",
    borderRadius: 3,
    overflow: "hidden",
    marginBottom: 15,
  },
  loadingProgress: {
    height: "100%",
    width: "0%",
    backgroundColor: "#28a745",
    borderRadius: 3,
    animation: "loading 3s linear forwards",
  },
  redirectText: {
    fontSize: 14,
    color: "#888",
    margin: 0,
  },
};

// Add CSS animations
if (typeof document !== "undefined" && document.styleSheets.length > 0) {
  const styleSheet = document.styleSheets[0];
  styleSheet.insertRule(
    `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `,
    styleSheet.cssRules.length
  );

  styleSheet.insertRule(
    `
    @keyframes loading {
      0% { width: 0%; }
      100% { width: 100%; }
    }
  `,
    styleSheet.cssRules.length
  );
}

export default Packages;